import LegalPageLayout from '../components/LegalPageLayout';

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 2025">
      <h2><span className="section-num">01.</span> INTRODUCTION</h2>
      <p>
        AIBL ("we", "us", "our") operates the aibl.money website, API, and associated services. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data.
      </p>
      <p>
        We are committed to minimizing data collection. As a decentralized infrastructure platform, we collect only what is necessary to operate the Service securely and effectively.
      </p>

      <h2><span className="section-num">02.</span> INFORMATION WE COLLECT</h2>
      
      <h3>2.1 INFORMATION YOU PROVIDE</h3>
      <ul>
        <li>Email address (if you sign up for early access, grants, or communications)</li>
        <li>Wallet addresses connected to the Service</li>
        <li>API credentials and associated metadata</li>
        <li>Agent identifiers and configuration data</li>
        <li>Communications you send to us (support emails, Discord messages)</li>
      </ul>

      <h3>2.2 INFORMATION COLLECTED AUTOMATICALLY</h3>
      <ul>
        <li>IP address and approximate geolocation (country level)</li>
        <li>Browser type, device type, and operating system</li>
        <li>Pages visited and interaction patterns on aibl.money</li>
        <li>API usage data: endpoints called, request frequency, response times</li>
        <li>Referral source (how you found us)</li>
      </ul>

      <h3>2.3 BLOCKCHAIN DATA</h3>
      <ul>
        <li>Public blockchain data is inherently transparent. Transaction hashes, wallet addresses, and on-chain activity are publicly visible on blockchain explorers.</li>
        <li>We may index and analyze public blockchain data related to AIBL smart contract interactions for operational and security purposes.</li>
      </ul>

      <h3>2.4 INFORMATION WE DO NOT COLLECT</h3>
      <ul>
        <li>We do not collect real names, physical addresses, or government IDs</li>
        <li>We do not collect private keys or seed phrases — ever</li>
        <li>We do not collect biometric data</li>
        <li>We do not collect financial account information (bank accounts, credit cards)</li>
      </ul>

      <h2><span className="section-num">03.</span> HOW WE USE YOUR INFORMATION</h2>
      <p>We use collected information for:</p>
      <ul>
        <li>Operating and maintaining the Service</li>
        <li>Processing API requests and managing agent wallets</li>
        <li>Monitoring system health, performance, and security</li>
        <li>Detecting and preventing fraud, abuse, and unauthorized access</li>
        <li>Calculating and distributing lending yields</li>
        <li>Managing the enaibl vault and loan lifecycle</li>
        <li>Communicating service updates, security alerts, and operational notices</li>
        <li>Improving the Service based on usage patterns</li>
        <li>Complying with legal obligations</li>
      </ul>
      <p>We do NOT use your information for:</p>
      <ul>
        <li>Targeted advertising</li>
        <li>Selling to third parties</li>
        <li>Building marketing profiles</li>
        <li>Unsolicited promotional communications (unless you opt in)</li>
      </ul>

      <h2><span className="section-num">04.</span> DATA SHARING</h2>
      <p>
        We do not sell your personal information. We may share data with:
      </p>
      <ul>
        <li><strong>Service providers:</strong> Infrastructure providers (hosting, monitoring) that process data on our behalf under confidentiality agreements</li>
        <li><strong>Security partners:</strong> When necessary to investigate fraud, abuse, or security incidents</li>
        <li><strong>Legal compliance:</strong> When required by law, regulation, subpoena, or court order</li>
        <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or asset sale, user data may be transferred to the successor entity</li>
      </ul>
      <p>
        Blockchain transactions are publicly visible by nature and are not considered "sharing" under this policy.
      </p>

      <h2><span className="section-num">05.</span> DATA RETENTION</h2>
      <ul>
        <li><strong>API usage logs:</strong> Retained for 90 days, then aggregated/anonymized</li>
        <li><strong>Wallet and agent data:</strong> Retained for the duration of your account plus 30 days after deletion</li>
        <li><strong>Lending records:</strong> Retained for 2 years for operational and compliance purposes</li>
        <li><strong>Communications:</strong> Retained for 1 year</li>
        <li><strong>Analytics data:</strong> Aggregated and anonymized within 30 days</li>
      </ul>
      <p>
        You may request deletion of your data by contacting us. Note that blockchain data cannot be deleted as it exists on public, immutable ledgers.
      </p>

      <h2><span className="section-num">06.</span> DATA SECURITY</h2>
      <p>We implement reasonable security measures including:</p>
      <ul>
        <li>ECC encryption for wallet private key management</li>
        <li>Admin authentication for internal dashboards</li>
        <li>Rate limiting and abuse detection on API endpoints</li>
        <li>Regular security reviews of smart contracts and infrastructure</li>
        <li>Encrypted data transmission (TLS/HTTPS)</li>
        <li>Access controls limiting employee access to user data</li>
      </ul>
      <p>
        No system is 100% secure. We cannot guarantee absolute security of your data. You are responsible for securing your API credentials and wallet access.
      </p>

      <h2><span className="section-num">07.</span> COOKIES AND TRACKING</h2>
      <p>aibl.money uses minimal cookies:</p>
      <ul>
        <li><strong>Essential cookies:</strong> Required for site functionality (session management)</li>
        <li><strong>Analytics:</strong> Anonymous usage analytics to understand site traffic (no personal identification)</li>
      </ul>
      <p>We do NOT use:</p>
      <ul>
        <li>Advertising cookies or trackers</li>
        <li>Cross-site tracking</li>
        <li>Social media tracking pixels</li>
        <li>Fingerprinting technologies</li>
      </ul>
      <p>
        You can disable cookies in your browser settings. Essential cookies are required for the Service to function.
      </p>

      <h2><span className="section-num">08.</span> YOUR RIGHTS</h2>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of the data we hold about you</li>
        <li><strong>Correction:</strong> Request correction of inaccurate data</li>
        <li><strong>Deletion:</strong> Request deletion of your data (subject to legal retention requirements)</li>
        <li><strong>Portability:</strong> Request your data in a machine-readable format</li>
        <li><strong>Objection:</strong> Object to certain processing of your data</li>
        <li><strong>Withdrawal:</strong> Withdraw consent for optional data processing</li>
      </ul>
      <p>
        To exercise these rights, contact us at <a href="mailto:hello@aibl.money">hello@aibl.money</a>. We will respond within 30 days.
      </p>

      <h2><span className="section-num">09.</span> INTERNATIONAL DATA</h2>
      <p>
        AIBL operates globally. Your data may be processed in jurisdictions other than your own. By using the Service, you consent to the transfer and processing of your data in these jurisdictions.
      </p>

      <h2><span className="section-num">10.</span> CHILDREN</h2>
      <p>
        The Service is not directed to individuals under 18. We do not knowingly collect data from minors. If we become aware that we have collected data from a minor, we will delete it promptly.
      </p>

      <h2><span className="section-num">11.</span> THIRD-PARTY LINKS</h2>
      <p>
        The Service may contain links to third-party websites, protocols, or services. We are not responsible for the privacy practices of third parties. Review their privacy policies before interacting with them.
      </p>

      <h2><span className="section-num">12.</span> CHANGES TO THIS POLICY</h2>
      <p>
        We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Material changes may be communicated via email or site notification.
      </p>

      <h2><span className="section-num">13.</span> CONTACT</h2>
      <p>For privacy-related questions or requests:</p>
      <ul>
        <li>Email: <a href="mailto:hello@aibl.money">hello@aibl.money</a></li>
        <li>Website: <a href="https://aibl.money">aibl.money</a></li>
        <li>Discord: <a href="https://discord.gg/aibl">discord.gg/aibl</a></li>
      </ul>
    </LegalPageLayout>
  );
}
